
export interface RecipeIngredient {
  stockId: number;
  amount: number; // Amount used per unit of product
}

export interface ProductVariant {
  id: string;                   // varyant kimliği (ör. 'small','medium','large') — number ID'lerle çakışmasın diye string
  name: string;                 // ekranda görünen ad (S / M / L vb.)
  price?: number;               // bu boyutun fiyatı; yoksa Product.price kullanılır
  recipe?: RecipeIngredient[];  // bu boyuta özel tam reçete; yoksa Product.recipe kullanılır
}

export interface Category {
  id: string;
  name: string;
  sortOrder: number;
  color?: string; // New: Hex code for POS color coding
}

export interface Product {
  docType?: 'product'; // Discriminator
  id: number;
  name: string;
  category: string; // Refers to Category.id
  price: number;
  image: string;
  stock?: number;
  trackStock?: boolean; // New: Explicitly toggle stock tracking
  description?: string;
  recipe?: RecipeIngredient[];
  customizable?: boolean; // Triggers the modifier modal
  milkStockAmount?: number; // Override for milk-based modifiers
  syrupPumpCount?: number; // Multiplier for syrup-based modifiers
  syrupOverrides?: Record<string, number>; // Map of ModifierOption.id -> PumpCount (Matrix)
  publicImageUrl?: string; // New: Cloud URL for QR Menu (keeps local image Base64)
  variants?: ProductVariant[];
}

export interface CartModifier {
  id: string;
  name: string;
  price: number;
  groupId?: number; // Link back to parent group
  groupName?: string; // Cached name for display
}

// New Configuration Types
export interface ModifierOption {
  id: string;
  name: string;
  price: number;
  stockId?: number; // Link to the raw material
  amount?: number;  // Amount to deduct from stock
}

export interface ModifierGroup {
  id: number;
  name: string;       // e.g. "Süt Tercihi"
  options: ModifierOption[];
  allowMultiple: boolean; // false = Radio (Single), true = Checkbox (Multiple)
  required?: boolean;  // Must select at least one?
  order?: number; // Display order
  isMilkSelect?: boolean; // New: If true, uses Product.milkStockAmount instead of option.amount
  isSyrupSelect?: boolean; // New: If true, uses Product.syrupPumpCount * option.amount
  categoryIds?: string[]; // If set, group only shows for products in these categories
}

export interface CartItem extends Product {
  quantity: number;
  notes?: string;
  modifiers?: CartModifier[];
  status?: 'served' | 'wastage' | 'cancelled' | 'comp' | 'paid';
  discountType?: 'percent' | 'amount';
  discountValue?: number;
  selectedVariantId?: string;

  // QR order audit/queue linkage
  qrOrderId?: string;
  qrOrderSource?: 'public_menu';

  // Masa bazlı sadakat: bu ürün hangi sadakat müşterisine ait
  loyaltyCustomerId?: string;
}

export interface Section {
  id: number;
  name: string;
  order?: number; // Madde 13: Bölüm sıralaması için
}

export interface Table {
  id: number;
  name: string;
  tempName?: string;
  x?: number; // Madde 10: X koordinatı
  y?: number; // Madde 10: Y koordinatı
  width?: number; // Madde 11: Masa genişliği
  height?: number; // Madde 11: Masa yüksekliği
  shape?: 'square' | 'rectangle' | 'round' | 'long'; // Madde 12: Masa şekli
  section: string; // Now refers to Section.name
  status: 'empty' | 'occupied' | 'paid' | 'reserved';
  capacity: number;
  orders?: CartItem[];
  incomingOrder?: CartItem[]; // New: Pending orders from QR Menu
  total?: number;
  time?: string;
  _updatedAt?: number;
  qrToken?: string; // QR menü linkinde tableId yerine kullanılan tahmin edilemez token
}

export interface MenuItem {
  name: string;
  icon: string;
  path: string;
  badge?: number;
  permission?: string; // Gatekeeper key
}

export interface StockItem {
  docType?: 'stockItem'; // Discriminator
  id: number;
  name: string;
  supplier: string;
  stock: number;
  unit: string;
  percent: number;
  cost: number;
  img: string;
  critical?: boolean;
  criticalThreshold?: number; // Custom limit for RED status
  warningThreshold?: number;  // Custom limit for YELLOW status
}

export interface TaxRate {
  id: number;
  name: string;
  rate: number;
  description: string;
}

export interface ExpenseCategory {
  id: number;
  name: string;
  description?: string;
  isRecurring?: boolean;
  recurringDay?: number;
  defaultAmount?: number;
  distributeMonthly?: boolean;
  reminderDaysBefore?: number;
  isWastage?: boolean;
}

export interface ExpenseTransaction {
  id: number;
  categoryId: number; // Foreign key to ExpenseCategory
  categoryName: string; // Cached name for display
  amount: number;
  description?: string;
  date: string; // ISO String
}

export interface Customer {
  id: string; // uuid
  name: string;
  phone?: string;
  email?: string;
  notes?: string;
  totalSpent?: number; // Cached metric
  debt?: number; // Cached metric
  lastVisit?: string;
  stamps?: number;
  loyaltyPoints?: number;
  loyaltyRedeemed?: number;
  loyaltyOnly?: boolean;
}
export interface CustomerMovementItemSummary {
  productId?: number;
  name: string;
  quantity: number;
  unitPrice: number;
  lineTotal: number;
  notes?: string;
}

export interface CustomerAccountMovement {
  id: string; // uuid
  customerId: string;
  type: 'debt_add' | 'payment' | 'adjustment' | 'void';
  amount: number;
  date: string; // ISO String
  note?: string;
  transactionId?: string; // Related sale/payment transaction
  tableId?: number | string;
  tableName?: string;
  staffId?: number;
  staffName?: string;
  source?: 'table_close' | 'manual' | 'debt_collection' | 'correction';
  status?: 'active' | 'cancelled';
  itemSummary?: CustomerMovementItemSummary[];
}
export enum PaymentMethod {
  Cash = 'Nakit',
  Card = 'Kredi Kartı',
  QR = 'QR / Mobil',
  MealCard = 'Yemek Kartı',
  CRM = 'CRM / Açık Hesap',
  Comp = 'İkram',
  Void = 'İptal / Zayi'
}

export interface Transaction {
  id: string;
  tableId?: number | string;
  customerId?: string; // New: Link to customer
  tableName: string;
  items: CartItem[];
  total: number;
  tax?: number; // New: Explicitly store calculated tax
  paymentMethod: PaymentMethod;
  payments?: { method: PaymentMethod; amount: number }[];
  date: string; // ISO String
  time: string; // HH:mm
  type?: 'order' | 'debt_payment' | 'expense'; // New: Classify transaction type
  status?: 'completed' | 'cancelled' | 'wastage';
  note?: string;
  isArchived?: boolean; // True if this transaction belongs to a closed Z-Report
  staffId?: number; // New: ID of the staff who processed it
  staffName?: string; // New: Name of the staff
  fulfillmentStatus?: 'preparing' | 'ready' | 'delivered'; // New: Self-Service Workflow
  line?: 1 | 2; // New: Queue Line Number
  orderRank?: number; // For manual sorting in Self Service View
  stockModelVersion?: 1;
  stockEffectsSource?: 'snapshot' | 'legacy_resolve';
  stockEffectsAppliedAt?: string; // ISO String
  stockEffects?: StockEffect[];
}
export interface StockEffect {
  stockId: number;
  delta: number; // Negative = deduction, positive = restore
  reason: 'recipe' | 'modifier' | 'manual';
  ref?: string;
}
export interface ZReport {
  id: string;
  date: string;
  total: number;
  cash: number;
  card: number;
  other: number;
  transactionsCount: number;
  timestamp: string;
  staff: string;
  wastageTotal?: number;
  tax?: number;
}

export interface ShiftReportExpense {
  description: string;
  amount: number;
  paymentSource: 'cash' | 'card' | 'personal';
}

export interface ShiftReport {
  id: string;
  cafeId: string;
  staffId: number;
  staffName: string;
  startTime: string;
  endTime: string;
  totalSales: number;
  cashTotal: number;
  cardTotal: number;
  otherTotal: number;
  transactionCount: number;
  physicalCash?: number;
  cashDifference?: number;
  handoverNote?: string;
  handoverTo?: string;
  expenses?: ShiftReportExpense[];
}

export type SubscriptionPlan =
  | 'trial'
  | 'basic'
  | 'management';

export interface SubscriptionDetails {
  plan: SubscriptionPlan;
  status: 'active' | 'past_due' | 'canceled';
  nextBillingDate: string; // ISO Date
}

export type SubscriptionFeature =
  | 'detailed_reports'      // Profit, Cost analysis
  | 'inventory_tracking'    // Stock amounts, critical thresholds
  | 'cost_tracking'         // Cost per unit, total value
  | 'recipes'               // Linking stock to products
  | 'online_services'       // QR Menu, Loyalty
  | 'staff_management'      // Adding/Editing staff
  | 'offline_mode'          // Working without internet
  | 'loyalty_program'       // Sadakat/puan programı
  | 'product_modifiers'     // Sıcaklık/Süt/Şurup/Ekstra secim gruplari
  | 'ai_assistant';         // KahveInn Asistan (AI destekli panel asistani)

export interface ReportSettings {
  showStockCost: boolean;
  showWastageLoss: boolean;
  operationalMode: boolean;
  showNetRevenue: boolean;
}

export interface AppSettings {
  appName?: string; // New: Custom App Title
  appAddress?: string;
// New: Address/Slogan
  isSetupComplete?: boolean; // New: Setup Wizard Flag
  autoZReport: boolean;
  autoShiftReport: boolean;
// New: Trigger shift report on user switch
  workingHoursStart?: number; // 0-23
  workingHoursEnd?: number;
// 0-23
  taxStrategy?: 'inclusive' | 'exclusive'; // New: Pricing Model
  currencySymbol?: string;
// New: Currency Symbol (e.g. ₺, $, €)
  uiScale?: number;
// New: UI Scaling Percentage (default 100)
  subscription?: SubscriptionDetails; // New: Subscription Tracking
  reportSettings?: ReportSettings;
// New: Persisted report toggles
  hiddenNavItems?: string[]; // New: List of hidden sidebar paths
  selfServiceMode?: boolean;
// New: Toggle for Self-Service View
  floorPlanMode?: boolean; // Madde 9: İsteğe bağlı 2D Kroki Modu
}

export interface QrMenuSettings {
  isActive: boolean;
  allowOrdering: boolean;
  wifiName?: string;
  wifiPassword?: string;
  themeColor: string; // Hex code
  headerText: string;
  soundEnabled?: boolean; // New: Audio alert preference
  slug?: string; // New: Custom URL slug (e.g. "kahveinn-izmir")
  latitude?: number; // Kafenin GPS enlemi (siparişte konum kontrolü için)
  longitude?: number; // Kafenin GPS boylamı (siparişte konum kontrolü için)
  orderRadiusMeters?: number; // İzin verilen sipariş mesafesi (varsayılan 200m)
}

export interface LoyaltySettings {
  mode: 'stamp' | 'points';
  stampsForReward: number;
  pointsForReward: number;
  pointsPerCurrency: number;
  rewardName: string;
  rewardProductIds?: string[];
  autoStamp: boolean;
  stampableCategories: string[];
}



export interface PermissionDefinition {
  key: string;
  label: string;
  description: string;
  category: 'Genel' | 'Satış' | 'Yönetim';
}

export interface Staff {
  id: number;
  name: string;
  status: 'working' | 'off';
  avatar: string;
  phone: string;
  dailySales?: number;
  pinCode?: string;
  hasPinCode?: boolean;
  permissions: string[];
  // PERMISSIONS dizisine yeni izin eklendiğinde eski personel kayıtlarının
  // "admin sayılamama" sorununu kalıcı çözmek için sabit bir işaret - bkz. isAdminMember (Staff.tsx).
  isAdmin?: boolean;
}

// --- CLOUD SYNC TYPES (From Backend) ---

export interface CloudMenuItem {
  id: string;
  name: string;
  price: number;
  tax: number;
  category: string;
}

export interface CloudOrder {
  id: string;
  cafeId: string;
  tableNumber: string;
  staffId: string;
  items: CloudMenuItem[];
  total: number;
  status: "pending" | "completed" | "cancelled";
  createdAt: any; // Firestore Timestamp
}

export interface CloudIncomingRequest {
  id: string;
  tableNumber: string;
  items: {
    name: string;
    quantity: number;
    note?: string
  }[];
  total: number;
}

// Period Reports — Detaylı Rapor modülü için
export type DatePreset = 'all' | 'today' | 'yesterday' | 'last7' | 'last30' | 'thisMonth' | 'lastMonth' | 'custom';

export interface DateRange {
  start: Date;
  end: Date;
  preset: DatePreset;
}

export interface PeriodKPI {
  current: number;
  previous: number;
  changePercent: number; // ((current - previous) / previous) * 100, previous=0 ise 0
}

export interface TopProduct {
  productId: string;
  productName: string;
  quantity: number;
  revenue: number;
}

export interface PaymentBreakdown {
  method: string;
  amount: number;
  percent: number;
}

export interface RevenueTrendPoint {
  date: string;  // formatDateISO çıktısı
  label: string; // formatDateTR çıktısı
  revenue: number;
}

export interface PeriodReportData {
  range: DateRange;
  revenue: PeriodKPI;
  transactionCount: PeriodKPI;
  averageTicket: PeriodKPI;
  averageTableSpending: PeriodKPI;
  grossProfit: PeriodKPI;
  netProfit: PeriodKPI;
  operatingExpenses: number;
  compTotal: number;     // ikram toplamı
  wastageTotal: number;  // zayi toplamı
  paymentBreakdown: PaymentBreakdown[];
  revenueTrend: RevenueTrendPoint[];
  topProducts: TopProduct[];
}
